import { useMemo } from 'react';

import { ActiveCase, isNewCase } from '#entities';

import { useGeoMap } from '../../../GeoMap';
import { COLLISION_CONFIG, measureLabelBox } from '../../../helpers';

import { useLabelForceLayout } from './useLabelForceLayout';
import { useMainMapOverlay } from './useMainMapOverlay';

export const useCaseLabelPosition = () => {
  const { projectionRef } = useGeoMap();
  const { newCasesOnMap, clusters, cases } = useMainMapOverlay();

  const allDots = useMemo(() => {
    const caseDots = cases.flatMap(c => {
      const dots: { id: string; x: number; y: number }[] = [];

      // Donor hospital
      const donorLonLat = c.donorHospital?.lonLat;
      if (donorLonLat) {
        const projected = projectionRef.current(donorLonLat);
        if (projected) {
          dots.push({ id: `donor-${c.id}`, x: projected[0], y: projected[1] });
        }
      }

      // Recipient hospital
      const recipientLonLat = c.recipientHospital?.lonLat;
      if (recipientLonLat) {
        const projected = projectionRef.current(recipientLonLat);
        if (projected) {
          dots.push({
            id: `recipient-${c.id}`,
            x: projected[0],
            y: projected[1],
          });
        }
      }

      return dots;
    });

    const newCaseDots = newCasesOnMap.flatMap(nc => {
      const lonLat = nc.donorHospital?.lonLat;
      if (!lonLat) {
        return [];
      }

      const projected = projectionRef.current(lonLat);
      if (!projected) {
        return [];
      }

      return [{ id: `newcase-${nc.id}`, x: projected[0], y: projected[1] }];
    });

    return [...caseDots, ...newCaseDots];
  }, [cases, newCasesOnMap, projectionRef]);

  const labelAnchors = useMemo(() => {
    const isCase = (data): data is ActiveCase =>
      'viewData' in data &&
      (typeof data.mapProgress === 'number' ||
        typeof data.progress === 'number');

    const pathEl = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'path',
    );

    return clusters.flatMap(cluster => {
      const data = cluster;
      const item = data.data;

      let x = 0;
      let y = 0;

      if (isCase(item) && item.viewData?.curve) {
        try {
          pathEl.setAttribute('d', item.viewData.curve);

          const total = pathEl.getTotalLength();
          const rawPct =
            typeof item.mapProgress === 'number'
              ? item.mapProgress
              : (item.progress ?? 0);

          const t = Math.min(1, Math.max(0, rawPct / 100));
          const len = t * total;
          const pt = pathEl.getPointAtLength(Math.min(Math.max(len, 0), total));
          x = pt.x;
          y = pt.y;
          // eslint-disable-next-line unused-imports/no-unused-vars
        } catch (error) {
          const projected = projectionRef.current?.(data.location);
          if (!projected) {
            return [];
          }
          x = projected[0];
          y = projected[1];
        }
      } else {
        const projected = projectionRef.current?.(data.location);
        if (!projected) {
          return [];
        }
        x = projected[0];
        y = projected[1];
      }

      // Label have different text depending on whether it's a new case or active
      const label = isNewCase(item) ? item.label : item.unosDonorId;

      return [
        {
          id: item.id,
          x,
          y,
          anchorX: x,
          anchorY: y,
          label,
        },
      ];
    });
  }, [clusters, projectionRef]);

  const layoutNodes = useMemo(() => {
    const labelNodes = labelAnchors.map(n => {
      const cluster = clusters.find(c => c.data.id === n.id);
      const hasTrial = Boolean(cluster?.data.trialProgram);
      const { width, height, radius } = measureLabelBox(n.label, 2, hasTrial);

      return {
        ...n,
        type: 'label' as const,
        width,
        height,
        radius,
      };
    });

    const dotNodes = allDots.map(d => ({
      id: `dot-${d.id}`,
      type: 'dot' as const,
      x: d.x,
      y: d.y,
      fx: d.x,
      fy: d.y,
      anchorX: d.x,
      anchorY: d.y,
      radius: COLLISION_CONFIG.DOT.radius,
    }));

    return [...dotNodes, ...labelNodes];
  }, [labelAnchors, allDots, clusters]);

  return useLabelForceLayout(layoutNodes);
};
